import type { GetSearchAnalyticsRawDataOutput } from "@/lib/ai/tools/get-search-analytics-raw-data/server";
import {
  buildDataset,
  buildFields,
  extractArray,
  isRecord,
  toDatasetRows,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import {
  type BindingPath,
  type RawCategoryField,
  readFirstRawBooleanText,
  readFirstRawNumber,
  readFirstRawText,
  resolveRawCategoryKey,
  resolveRawCategoryValue,
  sortRowsByPreferredMetric,
  toRawText,
} from "@/lib/ai/visualization/data/adapters/raw-mapping";
import type {
  VizDataset,
  VizDatasetRow,
  VizField,
  VizMetricAggregation,
} from "@/lib/ai/visualization/data/schema";

type MetricUnit = "count" | "percent" | "rank";

type MetricField = {
  key: string;
  comparedKey: string;
  differenceKey: string;
  currentPaths: readonly BindingPath[];
  comparedPaths: readonly BindingPath[];
  orderByField: string;
  unit: MetricUnit;
};

const CATEGORY_FIELDS: readonly RawCategoryField[] = [
  { key: "query", read: (item) => readFirstRawText(item, ["query"]) },
  { key: "url", read: (item) => readFirstRawText(item, ["url"]) },
  {
    key: "queryGroup",
    read: (item) =>
      readFirstRawText(item, [
        ["queryGroup", "name"],
        ["queryGroup", "id"],
      ]),
  },
  {
    key: "urlGroup",
    read: (item) =>
      readFirstRawText(item, [
        ["urlGroup", "name"],
        ["urlGroup", "id"],
      ]),
  },
  { key: "device", read: (item) => readFirstRawText(item, ["device"]) },
  { key: "country", read: (item) => readFirstRawText(item, ["country"]) },
  {
    key: "searchType",
    read: (item) => readFirstRawText(item, ["searchType"]),
  },
  { key: "date", read: (item) => readFirstRawText(item, ["date"]) },
  { key: "week", read: (item) => readFirstRawText(item, ["week"]) },
  { key: "month", read: (item) => readFirstRawText(item, ["month"]) },
  {
    key: "isAnonymizedQuery",
    read: (item) => readFirstRawBooleanText(item, ["isAnonymizedQuery"]),
  },
  {
    key: "isAnonymizedDiscover",
    read: (item) => readFirstRawBooleanText(item, ["isAnonymizedDiscover"]),
  },
] as const;

const METRICS: readonly MetricField[] = [
  {
    key: "clicks",
    comparedKey: "comparedClicks",
    differenceKey: "clicksDifference",
    currentPaths: ["clicks"],
    comparedPaths: ["comparedClicks"],
    orderByField: "CLICKS",
    unit: "count",
  },
  {
    key: "impressions",
    comparedKey: "comparedImpressions",
    differenceKey: "impressionsDifference",
    currentPaths: ["impressions"],
    comparedPaths: ["comparedImpressions"],
    orderByField: "IMPRESSIONS",
    unit: "count",
  },
  {
    key: "ctr",
    comparedKey: "comparedCtr",
    differenceKey: "ctrDifference",
    currentPaths: ["ctr"],
    comparedPaths: ["comparedCtr"],
    orderByField: "CTR",
    unit: "percent",
  },
  {
    key: "avgOfRank",
    comparedKey: "comparedAvgOfRank",
    differenceKey: "avgOfRankDifference",
    currentPaths: ["avgOfRank", "averageOfRank"],
    comparedPaths: ["comparedAvgOfRank", "comparedAverageOfRank"],
    orderByField: "AVERAGE_OF_RANK",
    unit: "rank",
  },
  {
    key: "distinctQueries",
    comparedKey: "comparedDistinctQueries",
    differenceKey: "distinctQueriesDifference",
    currentPaths: ["distinctQueries"],
    comparedPaths: ["comparedDistinctQueries"],
    orderByField: "DISTINCT_QUERIES",
    unit: "count",
  },
  {
    key: "distinctURLs",
    comparedKey: "comparedDistinctURLs",
    differenceKey: "distinctURLsDifference",
    currentPaths: ["distinctURLs", "distinctUrls"],
    comparedPaths: ["comparedDistinctURLs", "comparedDistinctUrls"],
    orderByField: "DISTINCT_URLS",
    unit: "count",
  },
  {
    key: "queryLength",
    comparedKey: "comparedQueryLength",
    differenceKey: "queryLengthDifference",
    currentPaths: ["queryLength"],
    comparedPaths: ["comparedQueryLength"],
    orderByField: "QUERY_LENGTH",
    unit: "count",
  },
] as const;

const ORDER_FIELD_TO_VALUE_KEY = Object.fromEntries(
  METRICS.flatMap((metric) => [
    [metric.orderByField, metric.key],
    [`${metric.orderByField}_DIFFERENCE`, metric.differenceKey],
  ]),
) as Record<string, string>;

const METRIC_UNIT_BY_KEY = Object.fromEntries(
  METRICS.flatMap((metric) => [
    [metric.key, metric.unit],
    [metric.comparedKey, metric.unit],
    [metric.differenceKey, metric.unit],
  ]),
) as Record<string, MetricUnit>;

const METRIC_FAMILY_BY_KEY = Object.fromEntries(
  METRICS.flatMap((metric) => [
    [metric.key, metric.key],
    [metric.comparedKey, metric.key],
    [metric.differenceKey, metric.key],
  ]),
) as Record<string, string>;

const TIME_CATEGORY_KEYS = ["date", "week", "month"] as const;

function resolveTimeCategoryKey(visibleCategoryKeys: string[]): string | null {
  return (
    TIME_CATEGORY_KEYS.find((key) => visibleCategoryKeys.includes(key)) ?? null
  );
}

function resolveGroupedSeriesKey(args: {
  categoryKey: string;
  visibleCategoryKeys: string[];
  timeCategoryKey: string | null;
}): string | null {
  if (!args.timeCategoryKey) return null;
  if (args.categoryKey !== args.timeCategoryKey) {
    return args.categoryKey;
  }

  return (
    args.visibleCategoryKeys.find((key) => key !== args.timeCategoryKey) ?? null
  );
}

const RAW_RATIO_AGGREGATION_BY_METRIC_KEY: Record<
  string,
  VizMetricAggregation
> = {
  ctr: {
    kind: "ratio",
    numeratorKey: "clicks",
    denominatorKey: "impressions",
  },
  comparedCtr: {
    kind: "ratio",
    numeratorKey: "comparedClicks",
    denominatorKey: "comparedImpressions",
  },
};

function resolveRawMetricAggregation(
  metricKey: string,
): VizMetricAggregation | undefined {
  return RAW_RATIO_AGGREGATION_BY_METRIC_KEY[metricKey];
}

function assignMetricValues(
  row: VizDatasetRow,
  item: Record<string, unknown>,
): void {
  for (const metric of METRICS) {
    const current = readFirstRawNumber(item, metric.currentPaths);
    const compared = readFirstRawNumber(item, metric.comparedPaths);

    if (current !== null) row[metric.key] = current;
    if (compared !== null) row[metric.comparedKey] = compared;
    if (current !== null && compared !== null) {
      row[metric.differenceKey] = current - compared;
    }
  }
}

function hasAnyMetricValue(row: VizDatasetRow): boolean {
  return METRICS.some((metric) => {
    return (
      typeof row[metric.key] === "number" ||
      typeof row[metric.comparedKey] === "number" ||
      typeof row[metric.differenceKey] === "number"
    );
  });
}

function resolvePreferredMetricKey(rawField: unknown): string | null {
  const field = toRawText(rawField)?.trim().toUpperCase();
  if (!field) return null;
  return ORDER_FIELD_TO_VALUE_KEY[field] ?? null;
}

function collectVisibleMetricKeys(rows: VizDatasetRow[]): string[] {
  const keys: string[] = [];
  const hasMetric = (key: string): boolean =>
    rows.some((row) => typeof row[key] === "number");

  for (const metric of METRICS) {
    if (hasMetric(metric.key)) keys.push(metric.key);
    if (hasMetric(metric.comparedKey)) keys.push(metric.comparedKey);
    if (hasMetric(metric.differenceKey)) keys.push(metric.differenceKey);
  }
  return keys;
}

function resolveRequestedMetricFamilyKeys(
  rawMetrics: unknown,
): string[] | null {
  if (!Array.isArray(rawMetrics)) return null;

  const families = new Set<string>();
  for (const rawMetric of rawMetrics) {
    const metricName = toRawText(rawMetric)?.trim().toUpperCase();
    const metric = METRICS.find(
      ({ orderByField }) => orderByField === metricName,
    );
    if (metric) families.add(metric.key);
  }
  return [...families];
}

function resolveDefaultValueKey(args: {
  preferredMetricKey: string | null;
  visibleMetricKeys: string[];
}): string | null {
  if (
    args.preferredMetricKey &&
    args.visibleMetricKeys.includes(args.preferredMetricKey)
  ) {
    return args.preferredMetricKey;
  }
  if (args.visibleMetricKeys.includes("clicks")) return "clicks";
  return args.visibleMetricKeys[0] ?? null;
}

function resolveCategoryFieldType(key: string): VizField["type"] {
  if (key === "date" || key === "week" || key === "month") {
    return "date";
  }
  return "string";
}

function collectCategoryValues(item: Record<string, unknown>): VizDatasetRow {
  const values: VizDatasetRow = {};
  for (const category of CATEGORY_FIELDS) {
    const value = category.read(item);
    if (value === null) continue;
    values[category.key] = value;
  }
  return values;
}

function collectVisibleCategoryKeys(rows: VizDatasetRow[]): string[] {
  return CATEGORY_FIELDS.map((category) => category.key).filter((key) =>
    rows.some((row) => {
      const value = row[key];
      return typeof value === "string" || typeof value === "number";
    }),
  );
}

/**
 * authoring対象のmetricから意味のあるscatter pairを投影できるか判定する。
 *
 * @remarks
 * 期間比較ではcurrent/previous/differenceを同じfamilyとして扱い、同系統pairを
 * 候補から外す。異なるfamilyの有限値が同じ2行以上で共存するときだけtrueを返し、
 * falseの場合はcallerが`scatter-plot`を`supportedViews`から除外する。
 */
function canProjectScatterPlot(args: {
  rows: VizDatasetRow[];
  visibleMetricKeys: string[];
  isComparing: boolean;
}): boolean {
  for (let xIndex = 0; xIndex < args.visibleMetricKeys.length; xIndex += 1) {
    const xKey = args.visibleMetricKeys[xIndex];
    if (!xKey) continue;
    for (
      let yIndex = xIndex + 1;
      yIndex < args.visibleMetricKeys.length;
      yIndex += 1
    ) {
      const yKey = args.visibleMetricKeys[yIndex];
      if (!yKey) continue;
      if (
        args.isComparing &&
        METRIC_FAMILY_BY_KEY[xKey] === METRIC_FAMILY_BY_KEY[yKey]
      ) {
        continue;
      }
      const plottablePairCount = args.rows.filter(
        (row) =>
          typeof row[xKey] === "number" &&
          Number.isFinite(row[xKey]) &&
          typeof row[yKey] === "number" &&
          Number.isFinite(row[yKey]),
      ).length;
      if (plottablePairCount >= 2) return true;
    }
  }
  return false;
}

function buildProfile(args: {
  isComparing: boolean;
  supportsBarViews: boolean;
  supportsGroupedTimeSeries: boolean;
  supportsScatterPlot: boolean;
}): NonNullable<VizDataset["profile"]> {
  return {
    datasetFamily: "sa.raw",
    variant: "ranking",
    compareMode: args.isComparing ? "period" : "none",
    supportedViews: [
      ...(args.supportsBarViews
        ? (["bar-comparison", "bar-ranking"] as const)
        : []),
      ...(args.supportsGroupedTimeSeries
        ? (["time-series-grouped-line"] as const)
        : []),
      ...(args.supportsScatterPlot ? (["scatter-plot"] as const) : []),
      "table",
    ],
  };
}

/**
 * SA raw tool 出力を可視化 dataset へ変換する。
 *
 * @remarks
 * 変換は `item -> row` の単純マッピングに固定し、行単位で数値が全欠損の場合のみ除外する。
 * authoring が `seriesByKey` を正しく選べるよう、category 候補は 1 つに潰さず rows/fields へ残す。
 */
export function buildSaRawDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  if (!isRecord(rawOutput)) return [];
  const output = rawOutput as Partial<GetSearchAnalyticsRawDataOutput> &
    Record<string, unknown>;
  const items = extractArray(output, "items");
  if (items.length === 0) return [];

  const categoryKey = resolveRawCategoryKey({
    items,
    categories: CATEGORY_FIELDS,
  });
  const context = isRecord(output.context) ? output.context : null;
  const isComparing = context?.isComparing === true;
  const requestedMetricFamilyKeys = resolveRequestedMetricFamilyKeys(
    context?.requestedMetrics,
  );
  const orderBy =
    isComparing && isRecord(context?.comparingOrderBy)
      ? context.comparingOrderBy
      : isRecord(context?.orderBy)
        ? context.orderBy
        : null;
  const preferredMetricKey = resolvePreferredMetricKey(
    isRecord(orderBy) ? orderBy.field : null,
  );
  const orderByDirection =
    (isRecord(orderBy) ? toRawText(orderBy.direction) : null) ?? null;

  const rows = toDatasetRows(items, (item) => {
    const categoryValues = collectCategoryValues(item);
    const categoryValue =
      (typeof categoryValues[categoryKey] === "string" ||
      typeof categoryValues[categoryKey] === "number"
        ? String(categoryValues[categoryKey])
        : null) ??
      resolveRawCategoryValue({
        item,
        selectedCategoryKey: categoryKey,
        categories: CATEGORY_FIELDS,
      });
    if (categoryValue === null) return null;

    const row: VizDatasetRow = {
      ...categoryValues,
      [categoryKey]: categoryValue,
    };
    assignMetricValues(row, item);
    return hasAnyMetricValue(row) ? row : null;
  });
  if (rows.length === 0) return [];

  const sortedRows = sortRowsByPreferredMetric({
    rows,
    categoryKey,
    preferredMetricKey,
    orderByDirection,
  });
  const visibleMetricKeys = collectVisibleMetricKeys(sortedRows);
  const requestedMetricFamilyKeySet = new Set(requestedMetricFamilyKeys ?? []);
  const constrainsChartMetrics =
    isComparing &&
    requestedMetricFamilyKeys !== null &&
    requestedMetricFamilyKeySet.size > 0;
  const chartMetricKeys = constrainsChartMetrics
    ? visibleMetricKeys.filter((metricKey) =>
        requestedMetricFamilyKeySet.has(
          METRIC_FAMILY_BY_KEY[metricKey] ?? metricKey,
        ),
      )
    : visibleMetricKeys;
  const defaultValueKey = resolveDefaultValueKey({
    preferredMetricKey,
    visibleMetricKeys: chartMetricKeys,
  });
  const visibleCategoryKeys = collectVisibleCategoryKeys(sortedRows);
  const timeCategoryKey = resolveTimeCategoryKey(visibleCategoryKeys);
  const groupedSeriesKey = resolveGroupedSeriesKey({
    categoryKey,
    visibleCategoryKeys,
    timeCategoryKey,
  });
  const supportsGroupedTimeSeries =
    !isComparing &&
    timeCategoryKey !== null &&
    groupedSeriesKey !== null &&
    defaultValueKey !== null;
  const supportsScatterPlot = canProjectScatterPlot({
    rows: sortedRows,
    visibleMetricKeys: chartMetricKeys,
    isComparing,
  });

  const fields = buildFields([
    ...visibleCategoryKeys.map((key) => ({
      key,
      role: "dim" as const,
      type: resolveCategoryFieldType(key),
    })),
    ...visibleMetricKeys.map((metricKey) => ({
      key: metricKey,
      role: "metric" as const,
      type: "number" as const,
      unit: METRIC_UNIT_BY_KEY[metricKey] ?? "count",
      metricFamily: METRIC_FAMILY_BY_KEY[metricKey] ?? metricKey,
      ...(resolveRawMetricAggregation(metricKey)
        ? { aggregation: resolveRawMetricAggregation(metricKey) }
        : null),
      ...(constrainsChartMetrics &&
      !requestedMetricFamilyKeySet.has(
        METRIC_FAMILY_BY_KEY[metricKey] ?? metricKey,
      )
        ? {
            internal: true,
            defaultVisible: false,
            exportable: false,
          }
        : null),
    })),
  ]);

  return [
    buildDataset({
      id: "sa-raw-ranking",
      profile: buildProfile({
        isComparing,
        supportsBarViews: defaultValueKey !== null,
        supportsGroupedTimeSeries,
        supportsScatterPlot,
      }),
      fields,
      rows: sortedRows,
      defaults: {
        category: categoryKey,
        ...(defaultValueKey ? { value: defaultValueKey } : null),
        ...(timeCategoryKey ? { x: timeCategoryKey } : null),
        ...(groupedSeriesKey ? { series: groupedSeriesKey } : null),
        ...(defaultValueKey ? { y: [defaultValueKey] } : null),
      },
      source,
    }),
  ];
}
